// Pure, and types the dashboard imports: nothing here may pull in server code.

/**
 * What a concluded test has written down beside its result, field by field: the homework that makes
 * its learning worth reading. Learnings run without it (decided 2026-10-20: weak tests are pooled,
 * since a missing hypothesis does not change what a test measured), so this only tells the team what
 * to fill in, before turning learnings on and on each test's debrief.
 */
export const FOUNDATION_FIELDS = ['hypothesis', 'description', 'mechanism', 'pxl', 'debrief'] as const;
export type FoundationFieldName = (typeof FOUNDATION_FIELDS)[number];

export type FoundationStatus = 'ok' | 'weak' | 'missing';

/** A field's status, with a short note when it is not ok ("Too short to test against"). */
export type FoundationField = { status: FoundationStatus; note: string | null };

export type TestFoundation = Record<FoundationFieldName, FoundationField>;

/** A hypothesis this short cannot say what should change, for whom, and why. */
const HYPOTHESIS_MIN_WORDS = 8;
/** A description this short says no more than the experiment's name. */
const DESCRIPTION_MIN_WORDS = 6;
/** Quoted back in the note when this short, so the team sees what they wrote. */
const QUOTE_MAX_CHARS = 24;

const words = (text: string): number => text.split(/\s+/).filter(Boolean).length;

const ok: FoundationField = { status: 'ok', note: null };

type TextFieldParams = { text: string | null; minWords: number; missingNote: string };

const textField = ({ text, minWords, missingNote }: TextFieldParams): FoundationField => {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return { status: 'missing', note: missingNote };
  if (words(trimmed) >= minWords) return ok;
  const quote = trimmed.length <= QUOTE_MAX_CHARS ? `“${trimmed}”. ` : '';
  return { status: 'weak', note: `${quote}Too short to test against.` };
};

export type FoundationInput = {
  hypothesis: string | null;
  description: string | null;
  /** Selected mechanism ids plus `custom` when one was written in (readMechanismIds). */
  mechanisms: readonly string[];
  /** Whether all three PXL scores were given. */
  scored: boolean;
  /** The debrief: absent, only boxes ticked, or with the team's own words. */
  debrief: 'none' | 'boxes-only' | 'written';
};

/** What a debrief holds, from the team's read (toTeamRead: null when it holds no answer at all). */
export const debriefKind = (
  read: { learning: string | null; explanation: string | null; outsideNote: string | null } | null,
): FoundationInput['debrief'] => {
  if (!read) return 'none';
  return read.learning || read.explanation || read.outsideNote ? 'written' : 'boxes-only';
};

export function foundationOf(input: FoundationInput): TestFoundation {
  return {
    hypothesis: textField({ text: input.hypothesis, minWords: HYPOTHESIS_MIN_WORDS, missingNote: 'No hypothesis' }),
    description: textField({
      text: input.description,
      minWords: DESCRIPTION_MIN_WORDS,
      missingNote: 'No description',
    }),
    mechanism: input.mechanisms.length > 0 ? ok : { status: 'missing', note: 'No expected mechanism' },
    pxl: input.scored ? ok : { status: 'missing', note: 'Not scored' },
    debrief:
      input.debrief === 'written'
        ? ok
        : input.debrief === 'boxes-only'
          ? { status: 'weak', note: 'Boxes ticked, nothing written' }
          : { status: 'missing', note: 'No debrief' },
  };
}

/** Whether anything is short of ok: the "need work" filter, and the confirmation before writing. */
export const needsWork = (foundation: TestFoundation): boolean =>
  FOUNDATION_FIELDS.some((field) => foundation[field].status !== 'ok');

/** The first note worth showing under a test's name, in field order. */
export const headlineNote = (foundation: TestFoundation): string | null =>
  FOUNDATION_FIELDS.map((field) => foundation[field])
    .filter((field) => field.status !== 'ok')
    .map((field) => field.note)
    .find((note) => note !== null) ?? null;
